import {
  Button,
  generateKey,
  ImageCropperModal,
  SUPPORTED_IMAGE_FORMATS,
  isCorrectFileSize,
  isSupportedFileType
} from '@cma/common'
import { useRef, useState, ChangeEvent, ReactNode, useEffect } from 'react'
import classNames from 'classnames'

type LayoutType = 'stacked' | 'inline'

type AdminImageUploaderProps = {
  previewSrc?: string
  name?: string
  rounded?: boolean
  layout?: LayoutType
  icon?: ReactNode
  allowRemove?: boolean
  label?: string
  disabled?: boolean
  onChange: (
    file: File | null,
    cropData: string | undefined,
    preview: string
  ) => void

  renderTrigger?: (props: { onClick: () => void }) => ReactNode
  renderPreview?: (previewUrl: string) => ReactNode
}

export default function AdminImageUploader({
  label = 'Upload Image',
  previewSrc,
  name,
  rounded = false,
  layout = 'stacked',
  icon,
  allowRemove = false,
  disabled = false,
  onChange,
  renderTrigger,
  renderPreview
}: AdminImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [fileKey, setFileKey] = useState(() => generateKey())
  const [showCropper, setShowCropper] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [preview, setPreview] = useState(previewSrc || '')
  const [error, setError] = useState<string>()

  useEffect(() => {
    setPreview(previewSrc || '')
  }, [previewSrc])

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    const [file] = files || []

    if (!file) return reset()

    if (!isSupportedFileType(files)) {
      setError('Unsupported file type')
      return
    }

    if (!isCorrectFileSize(files)) {
      setError('Your file must be smaller than 5MB')
      return
    }

    setSelectedFile(file)
    setShowCropper(true)

    e.target.value = ''
  }

  const reset = () => {
    setFileKey(generateKey())
    setSelectedFile(null)
    setPreview('')
    setError(undefined)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
    onChange(null, undefined, '')
  }

  const handleClick = () => {
    if (!disabled) {
      inputRef.current?.click()
    }
  }

  return (
    <>
      <div
        className={classNames('flex', {
          'flex-col items-center space-y-2': layout === 'stacked',
          'items-center space-x-4': layout === 'inline'
        })}>
        {renderPreview ? (
          renderPreview(preview)
        ) : preview ? (
          <img
            src={preview}
            alt={`${name || 'Image'} preview`}
            className={classNames('object-cover', {
              'h-24 w-24 rounded-full': rounded,
              'h-24 w-24 rounded-md': !rounded
            })}
          />
        ) : null}
        <input
          key={fileKey}
          type="file"
          accept={SUPPORTED_IMAGE_FORMATS.join(',')}
          className="sr-only"
          ref={inputRef}
          onChange={handleFileChange}
          disabled={disabled}
        />
        <div
          onClick={disabled || renderTrigger ? undefined : handleClick}
          className={classNames({
            'cursor-pointer': !disabled && !renderTrigger,
            'cursor-not-allowed opacity-50': disabled
          })}
          role={!renderTrigger ? 'button' : undefined}
          tabIndex={disabled || renderTrigger ? -1 : 0}
          onKeyDown={(e) => {
            if (
              !disabled &&
              !renderTrigger &&
              (e.key === 'Enter' || e.key === ' ')
            ) {
              e.preventDefault()
              handleClick()
            }
          }}>
          {renderTrigger ? (
            renderTrigger({ onClick: handleClick })
          ) : icon ? (
            <div
              className={classNames(
                'flex h-12 w-12 items-center justify-center rounded-full',
                {
                  'bg-gray-100 hover:bg-gray-200': !disabled,
                  'cursor-not-allowed bg-gray-50': disabled
                }
              )}>
              {icon}
            </div>
          ) : (
            <Button as="label" htmlFor={fileKey} disabled={disabled}>
              {label}
            </Button>
          )}
        </div>
        {allowRemove && preview && !disabled && (
          <button
            type="button"
            className="helix-text-brand text-sm"
            onClick={reset}>
            Remove
          </button>
        )}
        {error && (
          <div className="text-xs text-red-500" role="alert">
            {error}
          </div>
        )}
      </div>
      <ImageCropperModal
        rounded={rounded}
        image={selectedFile || undefined}
        isOpen={showCropper}
        onClose={() => {
          setShowCropper(false)
          reset()
        }}
        onConfirm={({ data, preview }) => {
          setPreview(preview || '')
          setShowCropper(false)
          onChange(selectedFile, JSON.stringify(data), preview || '')
        }}
      />
    </>
  )
}
